import { useAuthStore } from '@insightai/auth'
import { lazy } from 'react'
import { createBrowserRouter, Navigate } from 'react-router-dom'
import { AuthCallback, AuthGuard } from './AuthGuard'
import { RouteErrorElement } from './RouteErrorBoundary'
import { AppShell } from './shell/AppShell'

/**
 * F0-61 — every route is a lazy chunk.
 *
 * §9.1: the main bundle holds only shell, router, auth, tokens and primitives.
 * `lazy()` here is not an optimisation to revisit later; it is what keeps the
 * entry chunk inside the 200KB budget once 18 modules exist. Adding a route with
 * a static import would silently move that module's dependencies into the entry
 * chunk, which is why `pnpm size` runs on every PR (PE-07).
 *
 * Route paths follow §6.2 exactly, including the ones whose screens land in later
 * phases — those render a placeholder that names the phase. Registering the real
 * URLs now means deep links from designs, tickets and demos stay valid, and no
 * one has to renumber routes later.
 */
const WorkspaceHome = lazy(() => import('../features/workspace/WorkspaceHomePage'))
const SettingsPage = lazy(() => import('../features/workspace/SettingsPage'))
const PatternsGallery = lazy(() => import('../features/foundations/PatternsGalleryPage'))
const PhasePlaceholder = lazy(() => import('../features/foundations/PhasePlaceholderPage'))

// MOD-01 — Brief Builder. Each surface is its own chunk: the wizard, the TOSCA
// wizard and the structuring canvas are all reachable independently, and the
// canvas is the only one that pulls in d3 (PE-02).
const BriefList = lazy(() => import('../features/briefs/BriefListPage'))
const BriefWizard = lazy(() => import('../features/briefs/BriefWizardPage'))
const ToscaWizard = lazy(() => import('../features/briefs/tosca/ToscaWizardPage'))
const IssueTree = lazy(() => import('../features/briefs/structuring/IssueTreePage'))
const Assumptions = lazy(() => import('../features/briefs/assumptions/AssumptionsPage'))
const Design = lazy(() => import('../features/briefs/design/DesignPage'))
const ClientData = lazy(() => import('../features/briefs/clientData/ClientDataPage'))
const ReviewQueue = lazy(() => import('../features/briefs/review/ReviewQueuePage'))
const BriefProvenance = lazy(() => import('../features/briefs/provenance/BriefProvenancePage'))

/** Keeps the placeholder wiring declarative rather than 20 near-identical files. */
const placeholder = (title: string, phase: 1 | 2 | 3 | 4, requirement: string) => ({
  element: <PhasePlaceholder title={title} phase={phase} requirement={requirement} />,
})

export const router = createBrowserRouter([
  { path: '/auth/callback', element: <AuthCallback /> },

  {
    path: '/',
    element: (
      <AuthGuard>
        <AppShell />
      </AuthGuard>
    ),
    errorElement: <RouteErrorElement />,
    children: [
      { index: true, element: <WorkspaceRedirect /> },

      {
        path: 'w/:wsId',
        children: [
          { index: true, element: <WorkspaceHome /> },

          // MOD-01 — Brief Builder (Phase 1). Implemented.
          { path: 'briefs', element: <BriefList /> },
          // `briefs/new` is a redirect rather than a route: a brief has to exist
          // before it can be autosaved, so the list screen creates one and
          // navigates to it. A "new" route with no id would have nowhere to save.
          { path: 'briefs/new', element: <Navigate to=".." relative="path" replace /> },
          { path: 'briefs/:id', element: <BriefWizard /> },
          { path: 'briefs/:id/tosca', element: <ToscaWizard /> },
          { path: 'briefs/:id/issue-tree', element: <IssueTree /> },
          { path: 'briefs/:id/assumptions', element: <Assumptions /> },
          { path: 'briefs/:id/design', element: <Design /> },
          { path: 'briefs/:id/client-data', element: <ClientData /> },
          { path: 'briefs/:id/provenance', element: <BriefProvenance /> },
          { path: 'review-queue', element: <ReviewQueue /> },

          // MOD-02 — Secondary Research Engine (Phase 1)
          { path: 'secondary', ...placeholder('Secondary Desk', 1, 'SRE-FR-004–007') },
          { path: 'secondary/:studyId', ...placeholder('Knowledge feed', 1, 'SRE-FR-004–007, 019') },

          // MOD-03 — Survey Studio (Phase 2)
          { path: 'surveys', ...placeholder('Survey Studio', 2, 'SS-FR-001') },
          { path: 'surveys/:id/edit', ...placeholder('Survey editor', 2, 'SS-FR-003–005') },
          { path: 'surveys/:id/targeting', ...placeholder('Targeting & quotas', 2, 'SS-FR-006/007') },
          { path: 'surveys/:id/fieldwork', ...placeholder('Live fieldwork', 2, 'SS-FR-010, DB-FR-003') },
          { path: 'surveys/:id/analysis', ...placeholder('Cross-tab explorer', 2, 'SS-FR-011–013') },

          // MOD-03B/C/D
          { path: 'descriptive/:id', ...placeholder('Descriptive results', 4, 'DRD-FR-001–010') },
          { path: 'scaling/:id', ...placeholder('Scale Decision Wizard', 2, 'MSE-FR-003–013') },
          { path: 'questionnaire-ai/:id', ...placeholder('Questionnaire AI', 2, 'QDI-FR-001–018') },

          // MOD-04/05/06
          { path: 'sessions/:id', ...placeholder('Session detail', 3, 'VFG-FR-001–013') },
          { path: 'insights/:id', ...placeholder('Multimodal insights', 3, 'MIE-FR-001–020') },

          // MOD-07/08/09/10
          { path: 'kg', ...placeholder('Knowledge Graph', 1, 'KG-FR-002/003/006') },
          { path: 'reports/:id', ...placeholder('Report viewer', 1, 'RG-FR-001–014') },
          { path: 'copilot', ...placeholder('Research Copilot', 1, 'RC-FR-001–010') },
          { path: 'personas/:id', ...placeholder('AI Personas', 3, 'AP-FR-001–004') },
          { path: 'provenance', ...placeholder('Provenance audits', 1, 'BB-FR-026, MSE-FR-014') },

          // MOD-13
          { path: 'studies/:id', ...placeholder('Study detail', 1, 'DB-FR-002') },
          { path: 'settings', element: <SettingsPage /> },
          { path: 'settings/:section', element: <SettingsPage /> },
        ],
      },

      // Research Ops and Platform Admin sit outside the workspace scope.
      { path: 'ops/panel', ...placeholder('Panel health', 2, 'PM-FR-009') },
      { path: 'admin/model-registry', ...placeholder('Model registry', 1, 'AG-FR-001') },
      { path: 'admin/prompts', ...placeholder('Prompt registry', 1, 'AG-FR-004') },
      { path: 'admin/pricing', ...placeholder('Pricing rules', 1, 'BB-FR-004') },
      { path: 'admin/validation-rules', ...placeholder('Validation rules', 1, 'BB-FR-007') },

      // Phase 0 deliverable: the cross-cutting patterns, demonstrable in-app.
      { path: 'foundations', element: <PatternsGallery /> },

      { path: '*', element: <RouteErrorElement /> },
    ],
  },
])

/**
 * Sends a signed-in user to their active workspace.
 *
 * Falls back to /foundations rather than rendering an error when a user has no
 * workspace membership yet — which is a real state during onboarding, between
 * signup and workspace provisioning.
 */
function WorkspaceRedirect() {
  const workspaceId = useAuthStore(
    (state) => state.activeWorkspaceId ?? state.user?.workspaces[0]?.id ?? null,
  )
  return <Navigate to={workspaceId ? `/w/${workspaceId}` : '/foundations'} replace />
}
